import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SmoothScroll from "@/components/SmoothScroll";
import SEO from "@/components/SEO";
import {
  ArrowUpRight,
  Heart,
  Compass,
  ShieldCheck,
  Leaf,
  Sparkles,
  Map,
} from "lucide-react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const ABOUT_HERO = "https://images.unsplash.com/photo-1541064828014-503911d13103?auto=format&fit=crop&w=2400&q=80";

const VALUES = [
  {
    icon: Heart,
    title: "Local first",
    body: "Every driver, guide and host we partner with is born in the region we send you to. We pay above market rate, we book repeat — and our travelers feel the difference inside the first day.",
  },
  {
    icon: Compass,
    title: "Bespoke, never copy-pasted",
    body: "We do not sell catalogue tours. Each itinerary is sketched after a 30-minute call with you — pace, season, food preferences, fitness, the one experience you would regret not having.",
  },
  {
    icon: ShieldCheck,
    title: "Quiet logistics",
    body: "Private transfers, vetted resorts, 24-hour concierge from your time zone, and a single WhatsApp number for the whole trip. We make the moving parts disappear.",
  },
  {
    icon: Leaf,
    title: "Responsible by default",
    body: "Conservation-led marine partners, community-fee homestays, plastic-free island briefings. Travel done quietly is the only travel worth doing.",
  },
];

const STATS = [
  { n: "3+", label: "Years guiding inbound travelers" },
  { n: "38", label: "Indonesian provinces covered" },
  { n: "240+", label: "Local hosts and operators" },
  { n: "97%", label: "Repeat or referral guests" },
];

const TIMELINE = [
  {
    year: "2023",
    title: "A founder, a wife, an idea",
    body: "Windy Puji Oktiagraha — a professional web developer with five years of experience handling overseas clients and a quiet obsession with travel — sketches the first version of Across Indonesia with the support of his wife, Resa Dwi Anggraeni.",
  },
  {
    year: "2024",
    title: "A diver joins from Cilacap",
    body: "Garnida Anggit, a professional diver from Cilacap able to guide divers of any level, joins as co-founder. The marine and adventure desks are born — and our first inbound guests arrive.",
  },
  {
    year: "2025",
    title: "Friends across Indonesia",
    body: "We grow the way we wanted to: through friends. Drivers in Yogyakarta, hosts in Malang, boat captains in Komodo, photographers in Sumba — a quietly expanding circle of people we already trust.",
  },
  {
    year: "2026",
    title: "The journal",
    body: "We publish Across Indonesia — a digital journal sharing the routes, hosts and quiet places we used to keep to ourselves. Three years in, and only just beginning.",
  },
];

const TEAM = [
  {
    name: "Windy Puji Oktiagraha",
    role: "Founder · Web architect & traveler",
    img: "https://images.unsplash.com/photo-1531123897727-8f129e1688ce?auto=format&fit=crop&w=600&q=80",
  },
  {
    name: "Resa Dwi Anggraeni",
    role: "Co-founder · Concierge & host care",
    img: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=600&q=80",
  },
  {
    name: "Garnida Anggit",
    role: "Co-founder · Dive & marine lead · Cilacap",
    img: "https://images.unsplash.com/photo-1545167622-3a6ac756afa4?auto=format&fit=crop&w=600&q=80",
  },
  {
    name: "Friends across Indonesia",
    role: "Drivers, guides, hosts & captains",
    img: "https://images.unsplash.com/photo-1675206362603-b3c3c3ca47c6?auto=format&fit=crop&w=600&q=80",
  },
];

export default function About() {
  const root = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.utils.toArray(".fade-up").forEach((el) => {
        gsap.fromTo(
          el,
          { opacity: 0, y: 32 },
          {
            opacity: 1,
            y: 0,
            duration: 0.9,
            ease: "power3.out",
            scrollTrigger: { trigger: el, start: "top 88%" },
          }
        );
      });
    }, root);
    return () => ctx.revert();
  }, []);

  return (
    <SmoothScroll>
      <div ref={root} className="min-h-screen bg-[#FDFBF7]">
        <SEO
          title="About — A bespoke inbound agency, made in Indonesia"
          description="Across Indonesia is a small, local team designing slow, considered journeys for international travelers visiting Indonesia. Founded by Windy Puji Oktiagraha with his wife Resa Dwi Anggraeni and co-founder Garnida Anggit — three years in, supported by friends across the archipelago."
          image={ABOUT_HERO}
        />
        <Navbar transparent />

        {/* Hero */}
        <section className="relative h-[88vh] min-h-[600px] w-full overflow-hidden">
          <img
            src={ABOUT_HERO}
            alt="An Indonesian boatman on a traditional jukung"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/25 to-[#FDFBF7]" />
          <div className="relative z-10 h-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-end pb-24">
            <div className="t-overline text-white/85 mb-5">Who we are</div>
            <h1 className="t-display text-white max-w-5xl">
              A bespoke inbound agency,<br />
              <b>made in Indonesia.</b>
            </h1>
            <p className="mt-7 max-w-2xl text-white/85 text-lg leading-relaxed">
              We are a small, local team designing slow, considered journeys for
              international travelers visiting Indonesia. Bali to Raja Ampat,
              one quiet itinerary at a time.
            </p>
          </div>
        </section>

        {/* Mission */}
        <section className="max-w-7xl mx-auto px-6 md:px-12 py-32 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5 fade-up">
            <div className="t-overline text-seafoam mb-5">Our mission</div>
            <h2 className="t-h2 text-slate-900">
              Travel done <b>quietly.</b>
            </h2>
          </div>
          <div className="md:col-span-7 space-y-6 text-slate-700 text-lg leading-[1.85] fade-up">
            <p>
              Indonesia is seventeen thousand islands, three hundred languages,
              and one of the most generous cultures on earth. It is also one of
              the easiest places in the world to visit badly — to land in Bali,
              spend three days in a beach club, and leave without ever having
              met it.
            </p>
            <p>
              Across Indonesia was founded to do the opposite. We&apos;re young
              — three years in — but every road, host and quiet corner here
              comes from someone we already know. Windy started it with his
              wife Resa, brought in Garnida from Cilacap to lead the marine
              and dive desks, and grew the rest of the team the only way we
              trust: through friends.
            </p>
            <p>
              Our guests come from Europe, Australia, North America and
              Japan. They are honeymooners, families, divers, photographers,
              and leadership teams. They share one thing: the wish to meet
              Indonesia properly.
            </p>
          </div>
        </section>

        {/* Stats */}
        <section className="bg-white border-y border-stone-100">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 grid grid-cols-2 md:grid-cols-4 gap-10">
            {STATS.map((s) => (
              <div key={s.label} className="fade-up">
                <div className="text-5xl md:text-6xl font-light tracking-tight text-slate-900">
                  <b className="font-bold text-seafoam">{s.n}</b>
                </div>
                <div className="mt-3 text-sm text-slate-600 leading-snug max-w-[14ch]">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Values */}
        <section className="max-w-7xl mx-auto px-6 md:px-12 py-32">
          <div className="grid md:grid-cols-12 gap-12 items-start mb-16">
            <div className="md:col-span-5">
              <div className="t-overline text-seafoam mb-5 fade-up">What we believe</div>
              <h2 className="t-h2 text-slate-900 fade-up">
                Four <b>quiet</b><br />principles.
              </h2>
            </div>
            <p className="md:col-span-7 md:pt-12 text-slate-600 text-lg leading-relaxed fade-up">
              Four ideas have guided us from the first itinerary onwards.
              They shape every route we send and every operator we choose to
              work with.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-x-12 gap-y-14">
            {VALUES.map((v) => (
              <div key={v.title} className="fade-up flex gap-6">
                <div className="w-14 h-14 rounded-full bg-seafoam/10 border border-seafoam/20 flex items-center justify-center shrink-0">
                  <v.icon size={20} strokeWidth={1.5} className="text-seafoam" />
                </div>
                <div>
                  <h3 className="text-2xl font-light tracking-tight text-slate-900">
                    <b className="font-bold">{v.title}</b>
                  </h3>
                  <p className="mt-3 text-slate-600 leading-relaxed">{v.body}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Timeline */}
        <section className="bg-white border-y border-stone-100">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-32">
            <div className="t-overline text-seafoam mb-5 fade-up">Our story</div>
            <h2 className="t-h2 text-slate-900 mb-20 fade-up">
              From a founder&apos;s desk,<br />to <b>friends across Indonesia.</b>
            </h2>

            <ol className="relative border-l border-stone-200 pl-8 md:pl-12 space-y-14 max-w-3xl">
              {TIMELINE.map((t) => (
                <li key={t.year} className="fade-up relative">
                  <span className="absolute -left-[37px] md:-left-[53px] top-1.5 w-3.5 h-3.5 rounded-full bg-seafoam ring-4 ring-[#FDFBF7]" />
                  <div className="text-sm tracking-[0.25em] uppercase text-seafoam font-semibold">
                    {t.year}
                  </div>
                  <h3 className="mt-2 text-2xl md:text-3xl font-light text-slate-900 tracking-tight">
                    <b className="font-bold">{t.title}</b>
                  </h3>
                  <p className="mt-3 text-slate-600 leading-relaxed">{t.body}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* Team */}
        <section className="max-w-7xl mx-auto px-6 md:px-12 py-32">
          <div className="flex items-end justify-between mb-14">
            <div>
              <div className="t-overline text-seafoam mb-5 fade-up">The team</div>
              <h2 className="t-h2 text-slate-900 fade-up">
                The people who <b>answer your messages.</b>
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-12">
            {TEAM.map((m) => (
              <div key={m.name} className="fade-up">
                <div className="relative overflow-hidden rounded-2xl aspect-[3/4] bg-stone-100">
                  <img
                    src={m.img}
                    alt={m.name}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                </div>
                <h3 className="mt-4 text-lg font-light tracking-tight text-slate-900">
                  <b className="font-bold">{m.name}</b>
                </h3>
                <p className="mt-1 text-sm text-slate-500">{m.role}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-7xl mx-auto px-6 md:px-12 pb-32">
          <div className="rounded-3xl bg-slate-900 text-white px-8 md:px-16 py-20 md:py-24 relative overflow-hidden fade-up">
            <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-seafoam/20 blur-3xl" />
            <div className="relative max-w-2xl">
              <Sparkles size={22} strokeWidth={1.5} className="text-seafoam mb-6" />
              <h3 className="text-4xl md:text-5xl font-light tracking-tight leading-[1.05]">
                Ready to sketch <b className="font-bold">your Indonesia?</b>
              </h3>
              <p className="mt-6 text-white/75 text-lg leading-relaxed">
                A free 30-minute consultation. We will rough-out a route in your
                time zone, in plain English. No commitment, no catalogue.
              </p>
              <div className="mt-9 flex flex-wrap gap-3">
                <Link
                  to="/consultations"
                  className="btn-pill bg-white text-slate-900 hover:bg-stone-100 text-sm shadow-lg"
                >
                  Book a consultation <ArrowUpRight size={15} strokeWidth={1.5} />
                </Link>
                <Link
                  to="/blog"
                  className="btn-pill border border-white/30 text-white hover:bg-white/10 text-sm"
                >
                  <Map size={15} strokeWidth={1.5} /> Read the journal
                </Link>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </SmoothScroll>
  );
}
